import React from 'react';

import { classNames } from '@/helpers/classNames';
import constants from '@/theme/exportedVariables.module.scss';
import { isPlatform } from '@ionic/react';
import { useMediaQuery } from '@material-ui/core';

import styles from './Chip.module.scss';

/**
 * If the `color` prop is set to `"color-scheme"` then the Chip CSS would try to use
 * a `var(--color-scheme-base)` that should be defined from another CSS classes passed in via the
 * `className` prop
 */
export interface ChipProps {
	variant?: 'metro' | 'rounded' | 'outline' | 'outline-icon';
	disabled?: boolean;
	selected?: boolean;
	color?:
		| 'default'
		| 'light-gray'
		| 'dark-gray'
		| 'light-purple'
		| 'light-blue'
		| 'light-red'
		| 'gradient'
		| 'color-scheme';
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	leadingComponent?: React.ReactElement;
	trailingComponent?: React.ReactElement;
}

export const Chip: React.FC<ChipProps> = ({
	children,
	variant = 'metro',
	color = 'default',
	selected,
	disabled,
	className,
	onClick,
	leadingComponent,
	trailingComponent,
}) => {
	const isTablet = useMediaQuery(`(min-width: ${constants.sizeTablet}`);

	const classes = classNames([
		'PUI-Chip',
		styles['chip'],
		styles[`variant-${variant}`],
		styles[`color-${color}`],
		disabled && styles['disabled'],
		isTablet && onClick && styles['hover'],
		selected && styles['selected'],
		className,
	]);

	return (
		<div className={classes} onClick={onClick}>
			{leadingComponent && (
				<div className={styles['leading-component']}>
					{leadingComponent}
				</div>
			)}
			<span className={styles['label']}>{children}</span>
			{trailingComponent && (
				<div className={styles['trailing-component']}>
					{trailingComponent}
				</div>
			)}
		</div>
	);
};
